import React, { CSSProperties, ComponentProps, useEffect, useState } from 'react';
import { Dialog } from '../atoms';

type DialogPt = ComponentProps<typeof Dialog>['pt'];

type ConfirmationParams = {
  id: string;
  [key: string]: unknown;
};

type DialogState = ConfirmationParams & {
  resolve: (value: boolean) => void;
};

type ShowDialogInternal = (params: DialogState, options?: { forceClosePrev?: boolean }) => void;
/**
 * Dialogs per `id`, in mount order — the same id can be mounted twice (page and modal). The last
 * one is shown; unmounting it falls back to the previous one.
 */
const showDialogStacks: Map<string, ShowDialogInternal[]> = new Map();

// eslint-disable-next-line react-refresh/only-export-components
export const confirmDialog = (params: ConfirmationParams, options?: { forceClosePrev?: boolean }): Promise<boolean> => {
  return new Promise<boolean>((resolve) => {
    const { id } = params;
    const stack = showDialogStacks.get(id);
    const showDialogInternal = stack?.[stack.length - 1];
    if (showDialogInternal) {
      showDialogInternal({ ...params, resolve }, options);
    } else {
      console.error(`ConfirmationDialog component with id "${id}" is not mounted`);
      resolve(false);
    }
  });
};

type ConfirmationDialogProps = {
  id: string;
  style?: CSSProperties;
  forceClosePrev?: boolean;
  children: (
    params: DialogState,
    actions: {
      accept: () => void;
      reject: () => void;
    },
  ) => React.ReactNode;
  contentClassName?: string;
  footerClassName?: string;
  headerClassName?: string;
  className?: string;
  /** PrimeReact `pt` overrides forwarded to the Dialog — e.g. to bump the mask z-index
   * above an app-specific overlay layer. */
  pt?: DialogPt;
};

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  id,
  forceClosePrev,
  style,
  children,
  contentClassName,
  footerClassName,
  headerClassName,
  className,
  pt,
}) => {
  const [visible, setVisible] = useState(false);
  const [dialogState, setDialogState] = useState<DialogState | null>(null);

  useEffect(() => {
    let prevResolve: ((value: boolean) => void) | null = null;
    const showDialogInternal: ShowDialogInternal = (params, options) => {
      if ((options?.forceClosePrev || forceClosePrev) && prevResolve) {
        prevResolve(false);
      }
      setDialogState(params);
      setVisible(true);
      prevResolve = params.resolve;
    };
    const stack = showDialogStacks.get(id) ?? [];
    showDialogStacks.set(id, [...stack, showDialogInternal]);
    return () => {
      const remaining = (showDialogStacks.get(id) ?? []).filter((entry) => entry !== showDialogInternal);
      if (remaining.length > 0) {
        showDialogStacks.set(id, remaining);
      } else {
        showDialogStacks.delete(id);
      }
      if (prevResolve) prevResolve(false);
    };
  }, [id, forceClosePrev]);

  const handleAccept = () => {
    dialogState?.resolve(true);
    setVisible(false);
  };

  const handleReject = () => {
    dialogState?.resolve(false);
    setVisible(false);
  };

  if (!dialogState) return null;

  return (
    <Dialog
      visible={visible}
      onHide={handleReject}
      modal
      closable
      style={style}
      className={className}
      contentClassName={contentClassName}
      footerClassName={footerClassName}
      headerClassName={headerClassName}
      pt={pt}
    >
      {children(dialogState, { accept: handleAccept, reject: handleReject })}
    </Dialog>
  );
};
